import { Icon, DatePicker, TimePicker, Popconfirm } from 'antd';
import React, { useRef, useState, useEffect } from 'react';
import moment from 'moment';
import * as S from './styles';
import { withTranslation } from 'react-i18next';
import { JOB_DETAILS_UI } from '../TranslationValues';
import RejectionComment from './RejectionComment';

const NAMESPACES = ['JOB_DETAILS_UI'];

const ApprovalStatus = ({
    children,
    batch,
    showReschedule,
    setShowReschedule,
    newDate,
    newTime,
    handleDateChange,
    handleTimeChange,
    handleReschedule,
    handleApprove,
    handleAbort,
    handleReject,
    isApproving,
    isAborting,
    isRejecting,
    isRescheduling,
    users,
    showApprove,
    canAbortBatch,
    isBatchTimePassed,
    t,
}) => {
    const [showRejectComment, setShowRejectComment] = useState(false);
    const buttonGroupRef = useRef(null);
    const [displayStyle, setDisplayStyle] = useState('flex');
    const disablePastDates = (current) => {
        return current && current < moment().startOf('day');
    };
    

    useEffect(() => {
        if (buttonGroupRef.current) {
            const childCount = Array.from(
                buttonGroupRef.current.children
            ).filter(
                (child) =>
                    child?.tagName &&
                    child.tagName.toLowerCase().startsWith("button")
                ).length;
            setDisplayStyle(childCount === 1 ? 'block' : 'flex');
        }
    }, [children]);

    const isOnlyScheduled = batch?.status?.toLowerCase().startsWith('waiting_for_approval') && batch?.approvalType === 'USER' && !isBatchTimePassed;


    const openRejectConfirm = () => {
        setShowRejectComment(true);
    }

    const closeRejectConfirm = () => {
        setShowRejectComment(false);
    }

    return (
        <S.ApprovalCard>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <S.IconCircle bgColor="#41b8d5">
                    <Icon type="info-circle" style={{ fontSize: '24px', color: 'white' }} />
                </S.IconCircle>
                <S.CardContent>
                    {batch?.status === 'SCHEDULED'
                        ? <S.CardTitle>
                            {t(JOB_DETAILS_UI.SCHEDULED_TITLE.key, JOB_DETAILS_UI.SCHEDULED_TITLE.value)}
                        </S.CardTitle>
                        : <S.CardTitle>
                            {t(JOB_DETAILS_UI.APPROVAL_REQUIRED.key, JOB_DETAILS_UI.APPROVAL_REQUIRED.value)}
                        </S.CardTitle>}
                    <S.CardText>
                        {
                            batch?.status === 'SCHEDULED' || isOnlyScheduled
                                ? t(JOB_DETAILS_UI.SCHEDULED_DESCRIPTION.key, {
                                    defaultValue: JOB_DETAILS_UI.SCHEDULED_DESCRIPTION.value,
                                    feature: canAbortBatch && batch?.scheduledTime ? 'reschedule or abort' : canAbortBatch ? 'abort' : 'reschedule'
                                })
                                : (
                                    t(JOB_DETAILS_UI.APPROVAL_DESCRIPTION1.key, JOB_DETAILS_UI.APPROVAL_DESCRIPTION1.value) +
                                    ' ' +
                                    (batch?.approvalType && showApprove
                                    ? t(JOB_DETAILS_UI.APPROVAL_DESCRIPTION2.key, JOB_DETAILS_UI.APPROVAL_DESCRIPTION2.value)
                                    : '')
                                )
                        }
                        {batch?.approver && batch?.approvalType !== 'LEVEL' && <span style={{ display: 'block', marginTop: '4px', fontWeight: 600 }}>{t(JOB_DETAILS_UI.APPROVER_LABEL.key, JOB_DETAILS_UI.APPROVER_LABEL.value)} {users.find(user => user.id === parseInt(batch?.approver))?.displayName || ''}</span>}
                    </S.CardText>
                    
                    <S.ButtonGroup style={{position: 'relative', display: displayStyle }} ref={buttonGroupRef}>
                        {batch?.approvalType && showApprove && batch?.status !== 'SCHEDULED' &&
                        <S.PrimaryButton onClick={() => handleApprove()} disabled={isApproving || isAborting}>
                            <Icon type={isApproving ? "loading" : "check-circle"} />
                            {t(JOB_DETAILS_UI.APPROVE.key, JOB_DETAILS_UI.APPROVE.value)}
                        </S.PrimaryButton>}

                        {canAbortBatch
                        ? <RejectionComment
                            open={showRejectComment}
                            handleReject={handleAbort}
                            onCancelRejection={closeRejectConfirm}
                            style={{ left: '40px' }}
                            type="abort"
                          >
                            <S.DarkButton disabled={isApproving || isAborting} onClick={openRejectConfirm}>
                            <Icon type={isAborting ? "loading" : "close-circle"} />
                            {t(JOB_DETAILS_UI.ABORT.key, JOB_DETAILS_UI.ABORT.value)}
                            </S.DarkButton>
                          </RejectionComment>
                        : <RejectionComment
                            open={showRejectComment}
                            handleReject={handleReject}
                            onCancelRejection={closeRejectConfirm}
                            style={{ left: '40px' }}
                            type="reject"
                        >
                            {batch?.approvalType && showApprove && (batch?.status !== 'SCHEDULED' || canAbortBatch) &&
                            <S.DarkButton disabled={isApproving || isAborting} onClick={openRejectConfirm}>
                                <Icon type={isRejecting ? "loading" : "close-circle"} />
                                {t(JOB_DETAILS_UI.REJECT.key, JOB_DETAILS_UI.REJECT.value)}
                            </S.DarkButton>}
                        </RejectionComment>}
                        {batch?.scheduledTime && (
                            <S.SecondaryButton onClick={() => setShowReschedule(!showReschedule)} disabled={isRescheduling}>
                                <Icon type="clock-circle" />
                                {showReschedule ?
                                t(JOB_DETAILS_UI.CANCEL.key, JOB_DETAILS_UI.CANCEL.value) :
                                t(JOB_DETAILS_UI.RESCHEDULE.key, JOB_DETAILS_UI.RESCHEDULE.value)}
                            </S.SecondaryButton>)
                        }
                    </S.ButtonGroup>

                    {/* Reschedule Form */}
                    {showReschedule && (
                        <S.RescheduleForm>
                            <S.FormTitle>{t(JOB_DETAILS_UI.RESCHEDULE_JOB.key, JOB_DETAILS_UI.RESCHEDULE_JOB.value)}</S.FormTitle>
                            <S.DetailsGrid>
                                <S.DetailItem>
                                    <S.FormLabel>{t(JOB_DETAILS_UI.NEW_DATE.key, JOB_DETAILS_UI.NEW_DATE.value)}</S.FormLabel>
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        value={newDate ? moment(newDate) : null}
                                        onChange={handleDateChange}
                                        disabledDate={disablePastDates}
                                    />
                                </S.DetailItem>
                                <S.DetailItem>
                                    <S.FormLabel>{t(JOB_DETAILS_UI.NEW_TIME.key, JOB_DETAILS_UI.NEW_TIME.value)}</S.FormLabel>
                                    <TimePicker
                                        style={{ width: '100%' }}
                                        format="HH:mm"
                                        value={newTime ? moment(newTime, 'HH:mm') : null}
                                        onChange={handleTimeChange}
                                        disabledHours={() => {
                                            const now = moment();
                                            const selectedDate = newDate ? moment(newDate) : null;
                                            if (selectedDate && selectedDate.isSame(now, 'day')) {
                                                return Array.from({ length: now.hour() }, (_, i) => i);
                                            }
                                            return [];
                                        }}
                                        disabledMinutes={selectedHour => {
                                            const now = moment();
                                            const selectedDate = newDate ? moment(newDate) : null;
                                            if (selectedDate && selectedDate.isSame(now, 'day') && selectedHour === now.hour()) {
                                                return Array.from({ length: now.minute() + 1 }, (_, i) => i);
                                            }
                                            return [];
                                        }}
                                    />
                                </S.DetailItem>
                            </S.DetailsGrid>
                            <S.SaveButton onClick={handleReschedule} disabled={isRescheduling}>
                                {isRescheduling && <Icon type="loading" style={{ marginRight: '8px' }} />}
                                {t(JOB_DETAILS_UI.SAVE_NEW_SCHEDULE.key, JOB_DETAILS_UI.SAVE_NEW_SCHEDULE.value)}
                            </S.SaveButton>
                        </S.RescheduleForm>
                    )}
                </S.CardContent>
            </div>
        </S.ApprovalCard>
    );
};

export default withTranslation(NAMESPACES)(ApprovalStatus);
